import React from 'react';
import { WT_TOKENS } from '../../warm-transfer/config';
import type { WarmTransfer, WarmTransferHandled } from '../../warm-transfer/types';
import { Icon } from '../Icon/Icon';

function TransferCard({
  transfer,
  isSelected,
  onClick,
}: {
  transfer: WarmTransfer;
  isSelected: boolean;
  onClick: () => void;
}) {
  const isPriority = transfer.priority === 'priority';
  const palette = isPriority
    ? {
        border: 'rgba(168, 68, 46, 0.30)',
        bg: 'linear-gradient(180deg, rgba(168, 68, 46, 0.08), rgba(168, 68, 46, 0.04))',
        label: 'PRIORITY',
        labelColor: WT_TOKENS.negative,
        labelBg: 'rgba(168, 68, 46, 0.14)',
        dot: WT_TOKENS.negative,
        selectedShadow: `0 0 0 2px rgba(168, 68, 46, 0.22), ${WT_TOKENS.shadowMd}`,
        pulseDelay: '0ms',
      }
    : {
        border: WT_TOKENS.greenSoft,
        bg: `linear-gradient(180deg, ${WT_TOKENS.greenWash}, var(--cream-1) 80%)`,
        label: 'ACTIVE',
        labelColor: WT_TOKENS.greenDeep,
        labelBg: WT_TOKENS.greenSoft,
        dot: WT_TOKENS.green,
        selectedShadow: `0 0 0 2px ${WT_TOKENS.greenSoft}, ${WT_TOKENS.shadowMd}`,
        pulseDelay: '700ms',
      };

  return (
    <button
      type="button"
      onClick={onClick}
      className="relative mb-2.5 w-full cursor-pointer overflow-hidden rounded-[13px] border-[1.5px] p-4 pb-4 pt-3.5 text-left transition-[box-shadow,border-color,transform] duration-[220ms]"
      style={{
        background: palette.bg,
        borderColor: palette.border,
        boxShadow: isSelected ? palette.selectedShadow : WT_TOKENS.shadowXs,
        transform: isSelected ? 'translateY(-1px)' : 'none',
        color: 'var(--ink-0)',
      }}
    >
      <span className="wt-data-pulse" style={{ animationDelay: palette.pulseDelay }} />
      <div className="mb-2 flex items-center justify-between gap-2">
        <span
          className="inline-flex items-center gap-1.5 rounded-[5px] px-2 py-0.5 text-[9.5px] font-bold tracking-[0.10em]"
          style={{ background: palette.labelBg, color: palette.labelColor }}
        >
          <span
            className={isPriority ? 'wt-breathe' : ''}
            style={{ width: 5, height: 5, borderRadius: 999, background: palette.dot }}
          />
          {palette.label}
        </span>
        <span className="text-[11px] font-medium text-[var(--ink-2)]">{transfer.ago}</span>
      </div>
      <div className="mb-0.5 text-base font-semibold leading-[1.2] tracking-[-0.015em]">
        {transfer.patient.name}
      </div>
      <div className="text-[12.5px] font-medium tracking-[-0.005em] text-[var(--ink-1)]">
        {transfer.reason}
      </div>
    </button>
  );
}

function HandledRow({
  item,
  isSelected,
  onClick,
  isLast,
}: {
  item: WarmTransferHandled;
  isSelected: boolean;
  onClick: () => void;
  isLast: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="wt-row flex w-full cursor-pointer items-center gap-2.5 rounded-lg border-0 px-3 py-[11px] text-left"
      style={{
        background: isSelected ? 'var(--surface-mute)' : 'transparent',
        marginBottom: isLast ? 0 : 2,
      }}
    >
      <div className="min-w-0 flex-1">
        <div className="truncate text-[13px] font-semibold tracking-[-0.005em] text-[var(--ink-1)]">
          {item.patient}
        </div>
        <div className="mt-px text-[11px] text-[var(--ink-2)] opacity-70">{item.ago}</div>
      </div>
      <div
        className="inline-flex h-[18px] w-[18px] flex-none items-center justify-center rounded-full"
        style={{ background: WT_TOKENS.greenSoft, color: WT_TOKENS.greenDeep }}
      >
        <Icon kind="check-circ" size={11} stroke={2} />
      </div>
    </button>
  );
}

export interface WarmTransferRecentPanelProps {
  active: WarmTransfer[];
  handled: WarmTransferHandled[];
  selectedId: string | null;
  onSelect: (id: string) => void;
}

export function WarmTransferRecentPanel({
  active,
  handled,
  selectedId,
  onSelect,
}: WarmTransferRecentPanelProps) {
  return (
    <aside
      className="wt-fade-up wt-d-1 flex w-[360px] flex-none flex-col overflow-hidden rounded-2xl border border-[var(--cream-2)] bg-[var(--cream-1)] shadow-[var(--sh-1)]"
      style={{ maxHeight: 'calc(100vh - 180px)' }}
    >
      <header className="flex flex-none items-center justify-between gap-3 border-b border-[var(--cream-2)] px-[18px] py-4 pb-3.5">
        <h2 className="m-0 text-[15px] font-semibold tracking-[-0.015em] text-[var(--ink-0)]">
          Recent transfers
        </h2>
        {active.length > 0 && (
          <span
            className="inline-flex items-center gap-[5px] rounded-full px-[9px] py-0.5 text-[11px] font-semibold tracking-[-0.005em]"
            style={{ background: WT_TOKENS.negativeSoft, color: WT_TOKENS.negative }}
          >
            <span
              className="wt-breathe rounded-full"
              style={{ width: 5, height: 5, background: WT_TOKENS.negative }}
            />
            {active.length} active
          </span>
        )}
      </header>

      <div className="flex-1 overflow-auto px-3.5 py-3.5 pb-4">
        {active.length > 0 && (
          <div style={{ marginBottom: handled.length > 0 ? 14 : 0 }}>
            {active.map((tr) => (
              <TransferCard
                key={tr.id}
                transfer={tr}
                isSelected={selectedId === tr.id}
                onClick={() => onSelect(tr.id)}
              />
            ))}
          </div>
        )}

        {handled.length > 0 && (
          <>
            {active.length > 0 && (
              <div className="wt-eyebrow px-3 pb-2 text-[9.5px]">Handled</div>
            )}
            <div>
              {handled.map((it, i) => (
                <HandledRow
                  key={it.id}
                  item={it}
                  isSelected={selectedId === it.id}
                  onClick={() => onSelect(it.id)}
                  isLast={i === handled.length - 1}
                />
              ))}
            </div>
          </>
        )}

        {active.length === 0 && handled.length === 0 && (
          <div className="px-4 py-8 text-center text-[12.5px] leading-[1.55] text-[var(--ink-2)]">
            No transfers yet today.
          </div>
        )}
      </div>
    </aside>
  );
}
